import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, CheckCircle, AlertTriangle, Lightbulb, Target } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '@/utils/ky';
import { debounce } from '@/lib/utils';
import { getCustomLanguageFromURL } from '@/utils/customLanguageUtils';
import { Badge } from '@/components/ui/badge';

interface FeedbackTip {
  type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
  message: string;
  timestamp: Date;
  stage?: string; // NEW: Framework stage (e.g., "develop", "discover", "design")
  // To track which message this feedback corresponds to
  messageIndex: number;
}

interface InlineRealtimeFeedbackProps {
  transcript: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
  sessionId: string | null;
  hasCompetitorData: boolean;
  // This will be used to inject feedback tips
  setFeedbackTips: React.Dispatch<React.SetStateAction<FeedbackTip[]>>;
  apiBasePath?: string; // defaults to 'api/custom'
  transcriptRoleLabels?: { ai: string; user: string }; // defaults to { ai: 'Client', user: 'Salesperson' }
}

export function InlineRealtimeFeedback({
  transcript,
  sessionId,
  hasCompetitorData,
  setFeedbackTips,
  apiBasePath = 'api/custom',
  transcriptRoleLabels = { ai: 'Client', user: 'Salesperson' },
}: InlineRealtimeFeedbackProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const currentLanguage = getCustomLanguageFromURL();

  // Create a stable debounced function using useRef
  const debouncedAnalyzeRef = useRef(
    debounce(
      async (
        transcript: any[],
        sessionId: string,
        latestMessageIndex: number,
        hasCompetitorData: boolean,
        currentLanguage: string,
        setIsAnalyzing: React.Dispatch<React.SetStateAction<boolean>>,
        setFeedbackTips: React.Dispatch<React.SetStateAction<FeedbackTip[]>>,
      ) => {
        if (!sessionId || transcript.length === 0) return;

        // Only analyze if the latest message is from the user and there's at least one AI message
        const latestMessage = transcript[latestMessageIndex];
        const hasAIMessages = transcript.some((msg) => msg.source === 'ai');

        if (
          !latestMessage ||
          latestMessage.source !== 'user' ||
          !hasAIMessages
        )
          return;

        try {
          setIsAnalyzing(true);
          console.log(
            'Starting inline feedback analysis for message index:',
            latestMessageIndex,
          );

          const formattedTranscript = transcript
            .map(
              (msg) =>
                `${msg.source === 'ai' ? transcriptRoleLabels.ai : transcriptRoleLabels.user}: ${msg.message}`,
            )
            .join('\n');

          // Make API request
          const response = await api.post(
            `${apiBasePath}/sessions/${sessionId}/realtime-feedback`,
            {
              json: {
                transcript: formattedTranscript,
                hasCompetitorData,
                language: currentLanguage,
              },
              timeout: 30000,
            },
          );

          const analysis = (await response.json()) as {
            feedbackTips: Array<{
              type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
              message: string;
              stage?: string;
            }>;
          };
          console.log('Inline feedback API response:', analysis);

          if (analysis?.feedbackTips?.length > 0) {
            // Filter out any invalid types (accept all valid feedback types)
            const validTips = analysis.feedbackTips.filter((tip) =>
              ['warning', 'error', 'praise', 'guidance', 'coaching'].includes(
                tip.type,
              ),
            );

            const newTips = validTips.map((tip) => ({
              ...tip,
              timestamp: new Date(),
              messageIndex: latestMessageIndex,
            })) as FeedbackTip[];

            // Use requestAnimationFrame to schedule state update after current render
            requestAnimationFrame(() => {
              setFeedbackTips((prev) => [...prev, ...newTips]);
            });
            console.log(
              'Added inline feedback tips for message:',
              latestMessageIndex,
            );
          }
        } catch (error) {
          console.error('Error getting inline feedback:', error);
        } finally {
          setIsAnalyzing(false);
        }
      },
      2000,
    ),
  );

  // Wrapper function that uses the stable debounced function
  const analyzeTranscript = useCallback(
    (transcript: any[], sessionId: string, latestMessageIndex: number) => {
      debouncedAnalyzeRef.current(
        transcript,
        sessionId,
        latestMessageIndex,
        hasCompetitorData,
        currentLanguage,
        setIsAnalyzing,
        setFeedbackTips,
      );
    },
    [hasCompetitorData, currentLanguage, setFeedbackTips],
  );

  // Monitor for new user messages
  useEffect(() => {
    if (transcript.length === 0 || !sessionId) return;

    // Only proceed if there's at least one AI message in the transcript
    const hasAIMessages = transcript.some((msg) => msg.source === 'ai');
    if (!hasAIMessages) return;

    // Check if the last message is from the user
    const lastMessage = transcript[transcript.length - 1];
    if (lastMessage.source !== 'user') return;

    // Find the index of this last user message
    const lastUserMessageIndex = transcript.length - 1;

    console.log('Detected new user message at index:', lastUserMessageIndex);
    analyzeTranscript(transcript, sessionId, lastUserMessageIndex);
  }, [transcript, sessionId, analyzeTranscript]);

  // This component doesn't render anything directly
  return null;
}

// Component to render an inline feedback tip
export function InlineFeedbackTip({
  tip,
  delay = 0,
}: {
  tip: FeedbackTip;
  delay?: number;
}) {
  const getIcon = () => {
    switch (tip.type) {
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-blue-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'praise':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'guidance':
        return <Lightbulb className="w-4 h-4 text-purple-500" />;
      case 'coaching':
        return <Target className="w-4 h-4 text-blue-600" />;
      default:
        return null;
    }
  };

  const getColor = () => {
    switch (tip.type) {
      case 'warning':
        return 'border-blue-300 text-blue-700 bg-blue-50';
      case 'error':
        return 'border-red-300 text-red-800 bg-red-50';
      case 'praise':
        return 'border-green-300 text-green-700 bg-green-50';
      case 'guidance':
        return 'border-purple-300 text-purple-700 bg-purple-50';
      case 'coaching':
        return 'border-blue-400 text-blue-800 bg-blue-50';
      default:
        return 'border-slate-300 text-slate-700 bg-slate-50';
    }
  };

  // Clean the message of markdown formatting
  const cleanMessage = (message: string) => {
    // Remove markdown elements:
    // - Bold/italic markings: ** or __ or * or _
    // - Headers: # at beginning of line
    // - Feedback/warning prefixes
    return message
      .replace(/\*\*|\*|__|_/g, '') // Remove bold/italic
      .replace(/^#+\s*/g, '') // Remove headers
      .replace(/^Feedback:\s*/i, '') // Remove "Feedback:" prefix
      .replace(/^Warning:\s*/i, '') // Remove "Warning:" prefix
      .replace(/^Error:\s*/i, '') // Remove "Error:" prefix
      .replace(/^Praise:\s*/i, '') // Remove "Praise:" prefix
      .trim();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 0, x: 10 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      transition={{
        duration: 0.25,
        type: 'spring',
        stiffness: 120,
        damping: 14,
        delay: delay,
      }}
      className="flex justify-end my-1.5 relative"
    >
      <div
        className={`relative max-w-[80%] inline-flex items-start rounded-md border-l-[3px] ${getColor()} py-1.5 pl-2 pr-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}
        style={{ marginRight: 'calc(1rem + 8px)' }} // Align with user message
      >
        <div className="flex-shrink-0 mt-[3px] mr-1.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          {tip.stage && (
            <Badge className="mb-1 text-[10px] px-1.5 py-0">
              {tip.stage.toUpperCase()}
            </Badge>
          )}
          <p
            className={`text-xs ${
              getColor().split(' ')[1]
            } break-words leading-relaxed font-medium`}
          >
            {cleanMessage(tip.message)}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
